// PriceHistoryModal — Bir stoğun tedarikçi başına alış geçmişi.
// Kullanım:
//   <PriceHistoryModal
//     open={!!historyStock}
//     stockId={historyStock?.id}
//     stockName={historyStock?.name}
//     accountId={header.account_id} // opsiyonel: sadece bu tedarikçinin geçmişi
//     onClose={...}
//     onPick={(item) => /* unit_price ile satırı doldur */}
//   />
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Modal, Button } from './ui';
import { fmtAmount, fmtQty } from '../utils/numberFormat';

export default function PriceHistoryModal({ open, stockId, stockName, accountId, onClose, onPick }) {
  const [data, setData] = useState({ items: [], stats: null });
  const [loading, setLoading] = useState(false);
  const [filterCurrent, setFilterCurrent] = useState(!!accountId);

  const fetchHistory = useCallback(async () => {
    if (!stockId) return;
    setLoading(true);
    try {
      const r = await api.get('/purchase-insights/price-history', {
        params: {
          stock_id: stockId,
          ...(filterCurrent && accountId ? { account_id: accountId } : {}),
          limit: 30,
        },
      });
      if (r.data.success) setData(r.data.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [stockId, accountId, filterCurrent]);

  useEffect(() => { if (open) fetchHistory(); }, [open, fetchHistory]);

  if (!open) return null;

  return (
    <Modal title="📜 Fiyat Geçmişi" size="lg" onClose={onClose}
      overlayStyle={{ zIndex: 1100 }}
      footer={
        <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>Kapat</Button>
        </div>
      }>
      <div style={{ marginBottom: 14, padding: 12, background: 'var(--bg-card-hover)', borderRadius: 'var(--r-md)' }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: 'var(--text-primary)' }}>{stockName}</div>
        {accountId && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={filterCurrent} onChange={e => setFilterCurrent(e.target.checked)} />
              Sadece seçili tedarikçinin geçmişi
            </label>
          </div>
        )}
      </div>

      {/* İstatistikler */}
      {data.stats && data.stats.count > 0 && (
        <div style={S.statsGrid}>
          <div style={S.statCell}>
            <div style={S.statLabel}>Son Fiyat</div>
            <div style={{ ...S.statValue, color: 'var(--accent-primary)' }}>{fmtAmount(data.stats.last)} ₺</div>
          </div>
          <div style={S.statCell}>
            <div style={S.statLabel}>Ortalama</div>
            <div style={S.statValue}>{fmtAmount(data.stats.avg)} ₺</div>
          </div>
          <div style={S.statCell}>
            <div style={S.statLabel}>En Düşük</div>
            <div style={{ ...S.statValue, color: 'var(--success)' }}>{fmtAmount(data.stats.min)} ₺</div>
          </div>
          <div style={S.statCell}>
            <div style={S.statLabel}>En Yüksek</div>
            <div style={{ ...S.statValue, color: 'var(--danger)' }}>{fmtAmount(data.stats.max)} ₺</div>
          </div>
          <div style={S.statCell}>
            <div style={S.statLabel}>Trend</div>
            <div style={{ ...S.statValue, color: data.stats.trend > 0 ? 'var(--danger)' : 'var(--success)' }}>
              {data.stats.trend > 0 ? '↑' : data.stats.trend < 0 ? '↓' : '→'} %{Math.abs(data.stats.trend).toFixed(1)}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
      ) : !data.items.length ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)', fontStyle: 'italic' }}>
          Bu ürün için alış geçmişi yok.
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={S.th}>Tarih</th>
              <th style={S.th}>Belge No</th>
              <th style={S.th}>Tedarikçi</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Miktar</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Birim Fiyat</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Tutar</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(it => {
              const isReturn = it.invoice_type === 'purchase_return';
              const price = parseFloat(it.unit_price_net) || parseFloat(it.unit_price) || 0;
              return (
                <tr key={it.invoice_id} style={{ borderBottom: '1px solid var(--border-secondary)', opacity: isReturn ? 0.6 : 1 }}>
                  <td style={S.td}>{new Date(it.invoice_date).toLocaleDateString('tr-TR')}</td>
                  <td style={{ ...S.td, fontFamily: 'JetBrains Mono, monospace', fontSize: 'var(--text-xs)' }}>
                    {it.invoice_number}
                    {isReturn && <span style={{ color: 'var(--danger)', marginLeft: 4 }}>↩ İade</span>}
                  </td>
                  <td style={S.td}><strong>{it.account_title}</strong></td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtQty(it.quantity)}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
                    {fmtAmount(price)} {it.currency_code}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtAmount(it.line_total)}</td>
                  <td style={S.td}>
                    {onPick && !isReturn && (
                      <Button size="xs" variant="ghost" onClick={() => { onPick({ unit_price: price, account_id: it.account_id, account_title: it.account_title }); onClose(); }}>
                        Kullan
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

const S = {
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: 8,
    marginBottom: 14,
  },
  statCell: {
    padding: 10,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-sm)',
    textAlign: 'center',
  },
  statLabel: { fontSize: '0.65rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 4 },
  statValue: { fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'JetBrains Mono, monospace' },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' },
  td: { padding: '8px 10px' },
};
